import type { Workspace, RiskAgent, RiskNode, RiskFinding, RiskDetectorNode, RiskActionNode, AiIndicator, SignalBand } from "./types";
import { accountFields, assigneeLabel } from "./automation";

/* Client mirror of the server Risk Agent engine (api/src/riskagent/engine.ts).
 * Powers the builder's live preview offline; when the API is reachable the
 * server engine runs (and persists on a run), producing the same findings. */

const asNum = (v: unknown): number => (typeof v === "number" ? v : Number(v));
function testCondition(op: string, left: unknown, right: unknown): boolean {
  switch (op) {
    case "lt": return asNum(left) < asNum(right);
    case "lte": return asNum(left) <= asNum(right);
    case "gt": return asNum(left) > asNum(right);
    case "gte": return asNum(left) >= asNum(right);
    case "eq": return String(left) === String(right);
    case "neq": return String(left) !== String(right);
    case "contains": return String(left ?? "").toLowerCase().includes(String(right ?? "").toLowerCase());
    default: return false;
  }
}
const fill = (t: string, v: Record<string, string>): string => t.replace(/\{(\w+)\}/g, (_, k) => v[k] ?? `{${k}}`);

interface SourceRow { id: string; accountId: string; fields: Record<string, unknown>; }
function sourceRows(ws: Workspace, source: string): SourceRow[] {
  if (source === "accounts") return ws.accounts.map((a) => ({ id: a.id, accountId: a.id, fields: accountFields(a) }));
  if (source === "signals") return ws.signals.map((s) => ({ id: s.id, accountId: s.accountId, fields: { band: s.band, source: s.source, impact: s.impact, status: s.status, title: s.title } }));
  return (ws.records?.[source] ?? []).map((r) => ({ id: r.id, accountId: String(r.attributes.account ?? ""), fields: r.attributes as Record<string, unknown> }));
}
const themesOf = (f: Record<string, unknown>): string[] => {
  const t = f.themes;
  return (Array.isArray(t) ? t.map(String) : String(t ?? "").split(/,\s*/)).map((s) => s.trim().toLowerCase()).filter(Boolean);
};

export function analyzeAi(indicator: AiIndicator, fields: Record<string, unknown>): { confidence: number; reason: string } {
  const themes = themesOf(fields), sentiment = String(fields.sentiment ?? "").toLowerCase();
  const has = (kw: string) => themes.some((t) => t.includes(kw));
  switch (indicator) {
    case "negative_sentiment": return sentiment === "negative" ? { confidence: 0.82, reason: "Negative sentiment detected in the conversation" } : { confidence: 0, reason: "" };
    case "competitor_mention": return has("competitor") ? { confidence: 0.88, reason: "Competitor mentioned" } : { confidence: 0, reason: "" };
    case "sponsor_churn": return has("sponsor") ? { confidence: 0.9, reason: "Executive-sponsor churn language" } : { confidence: 0, reason: "" };
    case "escalation": return has("escalation") ? { confidence: 0.78, reason: "Unresolved escalation" } : { confidence: 0, reason: "" };
    case "churn_language": return has("renewal risk") || has("value gap") || has("churn") ? { confidence: 0.8, reason: "Churn / value-gap language" } : { confidence: 0, reason: "" };
    case "adoption_risk": return has("adoption") && sentiment !== "positive" ? { confidence: 0.72, reason: "Adoption concern raised" } : { confidence: 0, reason: "" };
    default: return { confidence: 0, reason: "" };
  }
}

const AI_LABEL: Record<AiIndicator, string> = {
  negative_sentiment: "Negative sentiment", competitor_mention: "Competitor mention", sponsor_churn: "Sponsor churn",
  escalation: "Escalation", churn_language: "Churn language", adoption_risk: "Adoption risk",
};
export const aiIndicatorLabel = (i: AiIndicator): string => AI_LABEL[i] ?? i;

function detect(det: RiskDetectorNode, row: SourceRow): { confidence: number; reason: string } | null {
  if (det.mode === "rule") {
    const ok = det.conditions.every((c) => testCondition(c.op, row.fields[c.field], c.value));
    if (!ok) return null;
    const summary = det.conditions.map((c) => `${c.field} ${c.op} ${c.value}`).join(" and ") || "rule matched";
    return { confidence: 1, reason: `Rule matched: ${summary}` };
  }
  const { confidence, reason } = analyzeAi(det.indicator, row.fields);
  return confidence >= det.minConfidence && confidence > 0 ? { confidence, reason } : null;
}
const bandRank = (b: SignalBand): number => (b === "risk" ? 0 : b === "watch" ? 1 : 2);

export function evaluateAgent(ws: Workspace, agent: RiskAgent): RiskFinding[] {
  const byId = new Map(agent.nodes.map((n) => [n.id, n] as const));
  const acctName = (id: string) => ws.accounts.find((a) => a.id === id)?.name ?? "";
  const findings: RiskFinding[] = [];
  const detectors = agent.nodes.filter((n): n is RiskDetectorNode => n.kind === "detector");
  for (const det of detectors) {
    const sources = agent.edges.filter((e) => e.to === det.id).map((e) => byId.get(e.from)).filter((n): n is RiskNode => n?.kind === "source");
    const actions = agent.edges.filter((e) => e.from === det.id).map((e) => byId.get(e.to)).filter((n): n is RiskActionNode => n?.kind === "action");
    if (!sources.length) continue;
    const best = new Map<string, { confidence: number; reason: string }>();
    for (const src of sources) for (const row of sourceRows(ws, (src as { source: string }).source)) {
      if (!row.accountId) continue;
      const m = detect(det, row);
      if (!m) continue;
      const cur = best.get(row.accountId);
      if (!cur || m.confidence > cur.confidence) best.set(row.accountId, m);
    }
    for (const [accountId, m] of best) {
      const accountName = acctName(accountId);
      const fActions: RiskFinding["actions"] = actions.map((act) => {
        if (act.type === "raise_signal") return { type: "raise_signal" as const, summary: `Raise ${det.band} signal: "${fill(act.title, { account: accountName })}"` };
        if (act.type === "create_task") return { type: "create_task" as const, summary: `Create task "${fill(act.title, { account: accountName })}" → ${assigneeLabel(ws, act.assignee)}` };
        return { type: "notify" as const, summary: `Notify ${assigneeLabel(ws, act.target)}: "${act.message}"` };
      });
      findings.push({ agentId: agent.id, accountId, accountName, detectorId: det.id, detectorLabel: det.label, band: det.band, reason: m.reason, confidence: m.confidence, actions: fActions });
    }
  }
  findings.sort((a, b) => (a.band === b.band ? b.confidence - a.confidence : bandRank(a.band) - bandRank(b.band)));
  return findings;
}
